import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DiamondIcon from "@mui/icons-material/Diamond";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import { getInterestingFactsRequest } from "../../redux/MuhuratCalender/muhuratSlice";
import { useAppTheme } from "../../theme/useAppTheme";

export default function GoldFactsPage() {
  const dispatch = useDispatch();
  const themeColors = useAppTheme();
  const { interestingFacts, factsLoading: loading, factsError: error } =
    useSelector((state) => state.muhurat || {});
  const facts = Array.isArray(interestingFacts?.data) ? interestingFacts.data : [];
  const [expandedFacts, setExpandedFacts] = useState({});

  useEffect(() => {
    dispatch(getInterestingFactsRequest());
  }, [dispatch]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && <Typography color="error">{error}</Typography>}

      {!loading && !error && facts.length === 0 && (
        <Box
          sx={{
            minHeight: "55vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 3,
          }}
        >
          <Typography
            sx={{
              color: themeColors.text,
              fontWeight: 500,
              fontSize: 16,
              lineHeight: 1.6,
              maxWidth: 420,
              opacity: 0.85,
            }}
          >
            Great things take time. We are building something special for you! A
            golden feature is on it's way ✨
          </Typography>
          <Typography
            sx={{
              color: themeColors.text,
              fontWeight: 600,
              fontSize: 18,
              letterSpacing: 1,
              mt: 3,
            }}
          >
            COMING SOON...
          </Typography>
        </Box>
      )}

      {!loading &&
        !error &&
        facts.map((fact, index) => {
          const factId = fact.id || fact._id || index;
          const title = fact.title || `Fact ${index + 1}`;
          const content = fact.content || "";
          const imageUrl = fact.image || "";
          const isExpanded = Boolean(expandedFacts[factId]);

          return (
            <Box
              key={factId}
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "22px",
                width: "100%",
                background: themeColors.bigContainer,
                border: "1px solid rgba(212, 175, 55, 0.55)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={title}
                    sx={{
                      width: "100%",
                      height: isExpanded ? 340 : 210,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <Box sx={{ height: 210 }} />
                )}

                <Box
                  sx={{
                    position: "absolute",
                    top: 14,
                    left: isExpanded ? "auto" : 14,
                    right: isExpanded ? 14 : "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    px: 1.4,
                    py: 0.7,
                    borderRadius: "20px",
                    background: "#F6C445",
                    color: "#1c1c1c",
                    fontWeight: 700,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: 16 }} />
                  Did You Know?
                </Box>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 2,
                  pt: 2,
                  pb: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      border: `1.5px solid ${themeColors.primary}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <DiamondIcon
                      sx={{ color: themeColors.primary, fontSize: 18 }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: themeColors.primary,
                      fontWeight: 700,
                      fontSize: 22,
                      lineHeight: 1.2,
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                    }}
                  >
                    {title}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: themeColors.text,
                    fontSize: 15,
                    whiteSpace: "pre-line",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: isExpanded ? "unset" : 3,
                    overflow: "hidden",
                  }}
                >
                  {content}
                </Typography>

                <Button
                  onClick={() =>
                    setExpandedFacts((current) => ({
                      ...current,
                      [factId]: !current[factId],
                    }))
                  }
                  endIcon={<ChevronRightIcon />}
                  sx={{
                    mt: 0.5,
                    borderRadius: "24px",
                    textTransform: "none",
                    color: themeColors.text,
                    border: "1px solid rgba(255,255,255,0.45)",
                    px: 2,
                    py: 0.7,
                    fontWeight: 500,
                  }}
                >
                  {isExpanded ? "Read Less" : "Read More"}
                </Button>

                <Box
                  sx={{
                    position: "absolute",
                    right: 14,
                    bottom: 18,
                    width: 78,
                    height: 58,
                    backgroundImage:
                      "radial-gradient(rgba(246, 196, 69, 0.85) 1.1px, transparent 1.2px)",
                    backgroundSize: "8px 8px",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </Box>
          );
        })}
    </Box>
  );
}
